// Local
import { booleanToYesNo, uniqueID } from '../utils/helper';
import {
  AccountAccess,
  AccountAddOnServices,
  AccountDetails,
  AccountFeatures,
  AccountInfo,
  AdditionalPowerHoldingRole,
  AdditionalRecipients,
  AssetManagementFormFields,
  AssetManager,
  AuthorizedAgentsFormFields,
  BeneficialOwnerFields,
  ClubbedCorporateSignerDetails,
  ClubbedEntityDetails,
  ClubbedParties,
  ContactDefaultTypes,
  ContactDetails,
  ControlPersonAndCorporateSecretary,
  CoOwnerAddress,
  CoOwnerFields,
  CorporateSignerDetails,
  CreateDraftDetails,
  DataInformation,
  DetailsFormFields,
  DraftAccountDetails,
  DraftContent,
  DraftSignerPartyDetails,
  EntityCategory,
  EntityDetailsFields,
  ExternalPartySource,
  FeaturesAndServices,
  FeePaymentInstructions,
  FiduciaryFields,
  FundingDetails,
  IAccountAccess,
  ICashSweep,
  IDataFeed,
  IDefaultRecipients,
  IFundingDetails,
  IReportingAnalytics,
  IReportingParameters,
  ITaxParameter,
  LegalEntityDocuments,
  OfficerAddress,
  OfficerFields,
  OfficerRole,
  Parties,
  PartyAddress,
  PaymentMethodInstructions,
  PowersIndicators,
  ProxyRecipientFormFields,
  ReferenceData,
  RulesControlPersonSecretary,
  RulesType,
  SignerPartyDetails,
  TrustAmendmentDate
} from '../utils/types';
import { checkPartyIsExternal, getLabelName, getLegalEntityCategory } from './common';

const getDocumentsData = (documents: LegalEntityDocuments[]): LegalEntityDocuments => {
  let returnObject: LegalEntityDocuments = {};
  if (documents && documents.length > 0) {
    returnObject = {
      governmentDocTypCd: documents[0]?.governmentDocTypCd,
      docIdentificationNum: documents[0]?.docIdentificationNum,
      docIdNo: documents[0]?.docIdentificationNum,
      docIssueDt: documents[0]?.docIssueDt,
      docExpirationDt: documents[0]?.docExpirationDt,
      docIssueCountryCd: documents[0]?.docIssueCountryCd,
      docIssueStateProvinceCd: documents[0]?.docIssueStateProvinceCd
    };
  }
  return returnObject;
};

const getPartieAddress = (address: PartyAddress[]): OfficerAddress => {
  let returnObject: OfficerAddress = {};
  if (address) {
    returnObject = {
      legalAddressId: address[0]?.addressId !== undefined ? String(address[0]?.addressId) : undefined,
      country: address[0]?.country,
      addressLineOne: address[0]?.addressLineOne,
      addressLineTwo: address[0]?.addressLineTwo,
      city: address[0]?.city,
      state: address[0]?.state,
      postalCode: address[0]?.postalCode,
      addressType: address[0]?.addressType
    };
  }
  return returnObject;
};

export const getAccountsAddress = (address: PartyAddress): OfficerAddress => ({
  legalAddressId: address?.addressId !== undefined ? String(address?.addressId) : undefined,
  country: address?.country,
  addressLineOne: address?.addressLineOne,
  addressLineTwo: address?.addressLineTwo,
  city: address?.city,
  state: address?.state,
  postalCode: address?.postalCode,
  addressType: address?.addressType
});

const getCoOwnerAddress = (address: PartyAddress[]): CoOwnerAddress => {
  let returnObject: CoOwnerAddress = {};
  if (address) {
    returnObject = {
      legalAddressId: address[0]?.addressId,
      legalCountry: address[0]?.country,
      legalAddressLineOne: address[0]?.addressLineOne,
      legalAddressLineTwo: address[0]?.addressLineTwo,
      legalCity: address[0]?.city,
      legalState: address[0]?.state,
      legalPostalCode: address[0]?.postalCode,
      legalAddressType: address[0]?.addressType,
      mailingAddressId: address[1]?.addressId,
      mailingCountry: address[1]?.country,
      mailingAddressLineOne: address[1]?.addressLineOne,
      mailingAddressLineTwo: address[1]?.addressLineTwo,
      mailingCity: address[1]?.city,
      mailingState: address[1]?.state,
      mailingPostalCode: address[1]?.postalCode,
      mailingAddressType: address[1]?.addressType
    };
  }
  return returnObject;
};

const getBeneficialOwnerDate = (DraftContent: DraftContent): EntityDetailsFields => {
  let returnObject: EntityDetailsFields = {};
  const { beneficialOwnerDate } = DraftContent;
  if (beneficialOwnerDate) {
    returnObject = { beneficialOwnerDate };
  }
  return returnObject;
};

const getAmendmentDate = (DraftContent: DraftContent): ClubbedEntityDetails => {
  const amendmentDate = DraftContent?.amendmentDate?.map(item => ({
    date: item,
    id: uniqueID()
  })) as TrustAmendmentDate[];
  return { amendmentDate };
};

const getTrustCertificationDetails = (draftContent: DraftContent): ClubbedEntityDetails => {
  let returnObject: ClubbedEntityDetails = {};
  if (draftContent?.legalEntityCategory === EntityCategory.TRUST_LE) {
    returnObject = {
      trustAmended: booleanToYesNo(draftContent?.amendmentInd),
      trustRevocable: booleanToYesNo(draftContent?.revocableInd),
      successorTrustee: booleanToYesNo(draftContent?.successorInd),
      grantorTrust: booleanToYesNo(draftContent?.grantorTrustInd)
    };
  }
  return returnObject;
};

const getEntityDetails = (draftContent: DraftContent): ClubbedEntityDetails => ({
  legalEntityCategory: getLegalEntityCategory(draftContent?.legalEntityCategory as string),
  taxationCountryCd: draftContent?.taxationCountryCd,
  taxIdentificationNumType: draftContent?.taxIdentificationNumType,
  legalAddMailAddChkInd: draftContent?.legalAddMailAddChkInd,
  address: draftContent?.address,
  legalEntitySubCategory: draftContent?.legalEntitySubCategory,
  otherSubCategory: draftContent?.otherSubCategory,
  fulLegalEntityName: draftContent?.fulLegalEntityName,
  shortenedLegalEntityName: draftContent?.shortenedLegalEntityName,
  incorporationCountryCd: draftContent?.incorporationCountryCd,
  incorporationStateProvinceCd: draftContent?.incorporationStateProvinceCd,
  orgIncorporationDt: draftContent?.orgIncorporationDt,
  isCountrySameAsDomicile: draftContent?.isCountrySameAsDomicile,
  taxIdentificationNum: draftContent?.taxIdentificationNum,
  taxIdentificationNumber: draftContent?.taxIdentificationNum,
  usTaxReturnInd: draftContent?.usTaxReturnInd,
  prfTaxExmptGrntdInd: draftContent?.prfTaxExmptGrntdInd,
  orgQualificationTypCd: draftContent?.orgQualificationTypCd,
  taxFinancialYearEndDate: draftContent?.taxFinancialYearEndDate,
  legalEntityIdentifier: draftContent?.legalEntityIdentifier,
  firstName: draftContent?.firstName,
  middleName: draftContent?.middleName,
  lastName: draftContent?.lastName,
  suffix: draftContent?.suffix,
  gender: draftContent?.gender,
  dateOfBirth: draftContent?.dateOfBirth,
  maritalStatus: draftContent?.maritalStatus,
  countryOfDomicile: draftContent?.countryOfDomicile,
  isSameCountryOfTax: draftContent?.isSameCountryOfTax,
  ...getDocumentsData(draftContent?.documents as LegalEntityDocuments[]),
  primaryPhoneNumberType: draftContent?.primaryPhoneNumberType || ContactDefaultTypes.DEFAULT_PHONE_TYPE,
  primaryPhoneNumber: draftContent?.primaryPhoneNumber,
  alternatePhoneNumberType: draftContent?.alternatePhoneNumberType || ContactDefaultTypes.DEFAULT_ALTERNATE_PHONE_TYPE,
  alternatePhoneNumber: draftContent?.alternatePhoneNumber,
  primaryEmailType: draftContent?.primaryEmailType || ContactDefaultTypes.DEFAULT_EMAIL_TYPE,
  primaryEmailAddress: draftContent?.primaryEmailAddress,
  soleNameOfMarriedIndividual: draftContent?.soleNameOfMarriedIndividual,
  accountPropWhenSoleNameOfMarriedIndv: draftContent?.accountPropWhenSoleNameOfMarriedIndv,
  ...getBeneficialOwnerDate(draftContent),
  externalPartySource: draftContent?.externalPartySource,
  externalPartyUlek: draftContent?.externalPartyUlek,
  externalPartyId: draftContent?.externalPartyId,
  ...getTrustCertificationDetails(draftContent),
  governingStateLaw: draftContent?.governingStateLaw,
  creationDate: draftContent?.creationDate,
  countryOfTax: draftContent?.countryOfTax,
  financialYearEnd: draftContent?.financialYearEnd,
  taxYearEnd: draftContent?.taxYearEnd,
  ...getAmendmentDate(draftContent)
});

const getCorporationPartyDetails = (officer: ClubbedParties) => {
  let returnObject = {};
  if (officer.partyType === RulesType.INDIVIDUAL_OFFICER || officer.partyType === RulesType.INDIVIDUAL_FIDUCIARY) {
    returnObject = {
      firstName: officer?.firstName,
      middleName: officer?.middleName ?? '',
      lastName: officer?.lastName,
      suffix: officer?.suffix ?? '',
      professionalTitle: officer?.professionalTitle ?? '',
      dateOfBirth: officer?.dateOfBirth,
      countryOfBirth: officer?.countryOfBirth,
      stateOfBirth: officer?.stateOfBirth,
      cityOfBirth: officer?.cityOfBirth,
      countryOfCitizenship: officer?.countryOfDomicile,
      countryOfDomicile: officer?.countryOfDomicile,
      ...getDocumentsData(officer?.documents as LegalEntityDocuments[]),
      legalAddressSelected: officer?.legalAddressSelected,
      primaryPhoneNumberType: officer?.primaryPhoneNumberType || ContactDefaultTypes.DEFAULT_PHONE_TYPE,
      primaryPhoneNumber: officer?.primaryPhoneNumber,
      alternatePhoneNumberType: officer?.alternatePhoneNumberType || ContactDefaultTypes.DEFAULT_ALTERNATE_PHONE_TYPE,
      alternatePhoneNumber: officer?.alternatePhoneNumber ?? '',
      primaryEmailType: officer?.primaryEmailType || ContactDefaultTypes.DEFAULT_EMAIL_TYPE,
      primaryEmailAddress: officer?.primaryEmailAddress,
      isBeneficialOwner: !!officer?.percentageOfOwnership,
      percentageOfOwnership: officer?.percentageOfOwnership ?? ''
    };
  } else {
    returnObject = {
      legalEntityCategory: getLegalEntityCategory(officer?.legalEntityCategory as string),
      legalEntitySubCategory: officer?.legalEntitySubCategory,
      otherSubCategory: officer?.otherSubCategory,
      fulLegalEntityName: officer?.fulLegalEntityName,
      shortenedLegalEntityName: officer?.shortenedLegalEntityName ?? '',
      incorporationStateProvinceCd: officer?.incorporationStateProvinceCd,
      countryOfCitizenship: officer?.incorporationCountryCd,
      countryOfDomicile: officer?.incorporationCountryCd,
      legalEntityIdentifier: officer?.legalEntityIdentifier ?? '',
      signerDetails: officer?.signers?.map((signer: ClubbedCorporateSignerDetails) => ({
        id: uniqueID(),
        firstName: signer?.firstName,
        middleName: signer?.middleName ?? '',
        lastName: signer?.lastName,
        suffix: signer?.suffix ?? '',
        professionalTitle: signer?.professionalTitle ?? '',
        dateOfBirth: signer?.dateOfBirth,
        countryOfDomicile: signer?.countryOfDomicile,
        taxIdentificationNumType: signer?.taxIdentificationNumType,
        taxIdentificationNum: signer?.taxIdentificationNum,
        taxIdentificationNumber: signer?.taxIdentificationNum,
        ...getPartieAddress(signer?.address as PartyAddress[]),
        primaryPhoneNumberType: signer?.primaryPhoneNumberType || ContactDefaultTypes.DEFAULT_PHONE_TYPE,
        primaryPhoneNumber: signer?.primaryPhoneNumber,
        alternatePhoneNumberType: signer?.alternatePhoneNumberType || ContactDefaultTypes.DEFAULT_ALTERNATE_PHONE_TYPE,
        alternatePhoneNumber: signer?.alternatePhoneNumber ?? '',
        primaryEmailType: signer?.primaryEmailType || ContactDefaultTypes.DEFAULT_EMAIL_TYPE,
        primaryEmailAddress: signer?.primaryEmailAddress,
        externalPartySource: signer?.externalPartySource,
        externalPartyUlek: signer?.externalPartyUlek,
        externalPartyId: signer?.externalPartyId,
        isMaintenanceRequired: signer?.isMaintenanceRequired || false
      }))
    };
  }
  return returnObject;
};

const getParties = (relatedParties: ClubbedParties[]): Parties => {
  const officers: OfficerFields[] = relatedParties
    .filter(
      (officer: ClubbedParties) =>
        officer?.partyType === RulesType.CORPORATION_OFFICER || officer?.partyType === RulesType.INDIVIDUAL_OFFICER
    )
    .map((officer: ClubbedParties) => ({
      officerId: uniqueID(),
      officerType: officer.partyType === RulesType.CORPORATION_OFFICER ? 'Yes' : 'No',
      ...getCorporationPartyDetails(officer),
      taxIdentificationNumType: officer?.taxIdentificationNumType,
      taxIdentificationNum: officer?.taxIdentificationNum,
      taxIdentificationNumber: officer?.taxIdentificationNum,
      externalPartySource: officer?.externalPartySource,
      externalPartyUlek: officer?.externalPartyUlek,
      externalPartyId: officer?.externalPartyId,
      isMaintenanceRequired: officer?.isMaintenanceRequired || false,
      ...getPartieAddress(officer?.address as PartyAddress[])
    }));

  const generateUniqueId = (owner: ClubbedParties): string => {
    let uniqueId: string = uniqueID();
    const officerObject: OfficerFields | undefined = officers.find(
      (officer: OfficerFields) =>
        officer.taxIdentificationNumber === owner.taxIdentificationNum && officer.isBeneficialOwner
    );
    if (officerObject) {
      uniqueId = officerObject.officerId as string;
    }
    return uniqueId;
  };

  const beneficialOwners: BeneficialOwnerFields[] = relatedParties
    .filter(
      (officer: ClubbedParties) =>
        officer?.partyType === RulesType.INDIVIDUAL_BENE_OWNER ||
        (officer?.partyType === RulesType.INDIVIDUAL_OFFICER && !!officer?.percentageOfOwnership)
    )
    .map((owner: ClubbedParties) => ({
      officerId: generateUniqueId(owner),
      percentageOfOwnership: owner?.percentageOfOwnership,
      firstName: owner?.firstName,
      middleName: owner?.middleName,
      lastName: owner?.lastName,
      suffix: owner?.suffix,
      dateOfBirth: owner?.dateOfBirth,
      countryOfDomicile: owner?.countryOfDomicile,
      ...getDocumentsData(owner?.documents as LegalEntityDocuments[]),
      ...getPartieAddress(owner?.address as PartyAddress[]),
      taxIdentificationNumType: owner?.taxIdentificationNumType,
      taxIdentificationNum: owner?.taxIdentificationNum,
      taxIdentificationNumber: owner?.taxIdentificationNum,
      externalPartySource: owner?.externalPartySource,
      externalPartyUlek: owner?.externalPartyUlek,
      externalPartyId: owner?.externalPartyId,
      isMaintenanceRequired: owner?.isMaintenanceRequired || false
    }));

  const authorizedAgents: AuthorizedAgentsFormFields[] = relatedParties
    .filter((officer: ClubbedParties) => officer?.partyType === RulesType.AUTHORIZED_AGENT)
    .map((agent: ClubbedParties) => ({
      agentId: uniqueID(),
      firstName: agent?.firstName,
      middleName: agent?.middleName,
      lastName: agent?.lastName,
      suffix: agent?.suffix,
      dateOfBirth: agent?.dateOfBirth,
      countryOfDomicile: agent?.countryOfDomicile,
      ssn: agent?.ssn,
      ...getDocumentsData(agent?.documents as LegalEntityDocuments[]),
      ...getPartieAddress(agent?.address as PartyAddress[]),
      primaryPhoneNumberType: agent?.primaryPhoneNumberType || ContactDefaultTypes.DEFAULT_PHONE_TYPE,
      primaryPhoneNumber: agent?.primaryPhoneNumber,
      alternatePhoneNumberType: agent?.alternatePhoneNumberType || ContactDefaultTypes.DEFAULT_ALTERNATE_PHONE_TYPE,
      alternatePhoneNumber: agent?.alternatePhoneNumber,
      primaryEmailType: agent?.primaryEmailType || ContactDefaultTypes.DEFAULT_EMAIL_TYPE,
      primaryEmailAddress: agent?.primaryEmailAddress,
      powers: agent?.fullPowerGranted ? 'Yes' : 'No',
      limitedPowers: agent?.limitedPowerGrantedList
    }));

  const coOwners: CoOwnerFields[] = relatedParties
    .filter((officer: ClubbedParties) => officer?.partyType === RulesType.INDIVIDUAL_CO_OWNER)
    .map((owner: ClubbedParties) => ({
      coOwnerId: uniqueID(),
      formOfCoOwnership: owner?.formOfCoOwnership,
      firstName: owner?.firstName,
      middleName: owner?.middleName,
      lastName: owner?.lastName,
      suffix: owner?.suffix,
      gender: owner?.gender,
      dateOfBirth: owner?.dateOfBirth,
      countryOfDomicile: owner?.countryOfDomicile,
      isSameCountryOfTax: owner?.isSameCountryOfTax,
      taxationCountryCd: owner?.taxationCountryCd,
      taxIdentificationNumType: owner?.taxIdentificationNumType,
      taxIdentificationNum: owner?.taxIdentificationNum,
      taxIdentificationNumber: owner?.taxIdentificationNum,
      ...getDocumentsData(owner?.documents as LegalEntityDocuments[]),
      primaryPhoneNumberType: owner?.primaryPhoneNumberType || ContactDefaultTypes.DEFAULT_PHONE_TYPE,
      primaryPhoneNumber: owner?.primaryPhoneNumber,
      alternatePhoneNumberType: owner?.alternatePhoneNumberType || ContactDefaultTypes.DEFAULT_ALTERNATE_PHONE_TYPE,
      alternatePhoneNumber: owner?.alternatePhoneNumber,
      primaryEmailType: owner?.primaryEmailType || ContactDefaultTypes.DEFAULT_EMAIL_TYPE,
      primaryEmailAddress: owner?.primaryEmailAddress,
      ...getCoOwnerAddress(owner.address as PartyAddress[]),
      legalAddMailAddChkInd: owner?.legalAddMailAddChkInd,
      externalPartySource: owner?.externalPartySource,
      externalPartyUlek: owner?.externalPartyUlek,
      externalPartyId: owner?.externalPartyId
    }));

  const fiduciary: OfficerFields[] = relatedParties
    ?.filter(
      (fiduciary: ClubbedParties) =>
        fiduciary?.partyType === RulesType.CORPORATE_FIDUCIARY ||
        fiduciary?.partyType === RulesType.INDIVIDUAL_FIDUCIARY
    )
    ?.map((fiduciary: ClubbedParties) => ({
      officerId: uniqueID(),
      fiduciaryType: fiduciary?.fiduciaryType,
      officerType: fiduciary.partyType === RulesType.CORPORATE_FIDUCIARY ? 'Yes' : 'No',
      ...getCorporationPartyDetails(fiduciary),
      taxIdentificationNumType: fiduciary?.taxIdentificationNumType,
      taxIdentificationNum: fiduciary?.taxIdentificationNum,
      taxIdentificationNumber: fiduciary?.taxIdentificationNum,
      externalPartySource: fiduciary?.externalPartySource,
      externalPartyUlek: fiduciary?.externalPartyUlek,
      externalPartyId: fiduciary?.externalPartyId,
      ...getPartieAddress(fiduciary?.address as PartyAddress[]),
      powersRoles: fiduciary?.powers as string[],
      powersIndicator:
        fiduciary?.powersIndicator === PowersIndicators.FULL
          ? PowersIndicators.FULL_POWERS
          : PowersIndicators.LIMITED_POWERS,
      isMaintenanceRequired: fiduciary?.isMaintenanceRequired || false,
    }));

  return { officers, beneficialOwners, authorizedAgents, coOwners, fiduciary } as Parties;
};

const checkIsControlPersonSecretary = (
  signer: CorporateSignerDetails,
  personObject: RulesControlPersonSecretary
): boolean => {
  if (checkPartyIsExternal(signer?.externalPartySource as string, signer?.externalPartyUlek as string)) {
    return (
      signer?.externalPartyUlek === personObject?.externalPartyUlek &&
      personObject?.partyType as RulesType === RulesType.INDIVIDUAL_OFFICER
    );
  } else {
    return (
      signer?.taxIdentificationNumber === personObject?.taxIdentificationNum &&
      personObject?.partyType as RulesType === RulesType.INDIVIDUAL_OFFICER
    );
  }
};

const getControlPersonSecretary = (
  officers: OfficerFields[],
  controlPersonObject: RulesControlPersonSecretary,
  secretaryObject: RulesControlPersonSecretary
): ControlPersonAndCorporateSecretary => {
  let controlPerson: OfficerRole = {};
  let corporateSecretary: OfficerRole = {};
  officers.forEach((officer: OfficerFields) => {
    if (officer.officerType === 'Yes') {
      officer.signerDetails?.forEach((signer: CorporateSignerDetails) => {
        if (checkIsControlPersonSecretary(signer, controlPersonObject)) {
          controlPerson = {
            id: signer.id,
            officerId: officer.officerId,
            ssn: controlPersonObject.taxIdentificationNum,
            externalPartySource: controlPersonObject?.externalPartySource,
            externalPartyUlek: controlPersonObject?.externalPartyUlek,
            externalPartyId: controlPersonObject?.externalPartyId,
            partyType: RulesType.CORPORATE_SIGNER
          };
        }
        if (checkIsControlPersonSecretary(signer, secretaryObject)) {
          corporateSecretary = {
            id: signer.id,
            officerId: officer.officerId,
            ssn: secretaryObject.taxIdentificationNum,
            externalPartySource: secretaryObject?.externalPartySource,
            externalPartyUlek: secretaryObject?.externalPartyUlek,
            externalPartyId: secretaryObject?.externalPartyId,
            partyType: RulesType.CORPORATE_SIGNER
          };
        }
      });
    } else {
      if (checkIsControlPersonSecretary(officer, controlPersonObject)) {
        controlPerson = {
          id: officer.officerId as string,
          officerId: officer.officerId,
          ssn: controlPersonObject.taxIdentificationNum,
          externalPartySource: controlPersonObject?.externalPartySource,
          externalPartyUlek: controlPersonObject?.externalPartyUlek,
          externalPartyId: controlPersonObject?.externalPartyId,
          partyType: RulesType.INDIVIDUAL_OFFICER
        };
      }
      if (checkIsControlPersonSecretary(officer, secretaryObject)) {
        corporateSecretary = {
          id: officer.officerId as string,
          officerId: officer.officerId,
          ssn: secretaryObject.taxIdentificationNum,
          externalPartySource: secretaryObject?.externalPartySource,
          externalPartyUlek: secretaryObject?.externalPartyUlek,
          externalPartyId: secretaryObject?.externalPartyId,
          partyType: RulesType.INDIVIDUAL_OFFICER
        };
      }
    }
  });
  return { controlPerson, corporateSecretary };
};

export const getContactDetails = (contactDetailsArray: ContactDetails[]): ContactDetails => {
  const returnObject: ContactDetails = {};
  contactDetailsArray?.forEach((contactDetails: ContactDetails) => {
    returnObject.primaryEmailType = contactDetails?.primaryEmailType || ContactDefaultTypes.DEFAULT_EMAIL_TYPE;
    if (contactDetails?.primaryEmailAddress) returnObject.primaryEmailAddress = contactDetails?.primaryEmailAddress;
    returnObject.primaryPhoneNumberType = contactDetails?.primaryPhoneNumberType || ContactDefaultTypes.DEFAULT_PHONE_TYPE;
    if (contactDetails?.primaryPhoneNumber) returnObject.primaryPhoneNumber = contactDetails?.primaryPhoneNumber;
    returnObject.alternatePhoneNumberType = contactDetails?.alternatePhoneNumberType || ContactDefaultTypes.DEFAULT_ALTERNATE_PHONE_TYPE;
    if (contactDetails?.alternatePhoneNumber) returnObject.alternatePhoneNumber = contactDetails?.alternatePhoneNumber;
  });

  return returnObject;
};

const getFeePayment = (
  assetManager: AssetManager,
  refPaymentMethodTypeList: ReferenceData[]
): AssetManagementFormFields => {
  let returnObject: AssetManagementFormFields = {};
  if (assetManager) {
    const { assetManagerFeePaymentAuthChkInd, feePaymentInstructions } = assetManager;
    if (assetManagerFeePaymentAuthChkInd) {
      returnObject = {
        percentPrinciple: feePaymentInstructions?.feePercentageBasedOnPrincipal,
        percentIncome: feePaymentInstructions?.feePercentageBasedOnIncomeOnInvestment,
        dummyIncome: feePaymentInstructions?.feePercentageBasedOnIncomeOnInvestment,
        beneficiaryName: feePaymentInstructions?.paymentMethodInstructions?.accountName,
        beneficiaryAccNumber: feePaymentInstructions?.paymentMethodInstructions?.accountNumber,
        forFutureCredit: feePaymentInstructions?.paymentMethodInstructions?.addDetailFinalRecipient,
        reference: feePaymentInstructions?.paymentMethodInstructions?.addDetailIntermediaryBank,
        paymentMethodValue: feePaymentInstructions?.paymentMethodInstructions?.paymentMethod,
        feePaymentMethod: getReferenceDataLabel(
          refPaymentMethodTypeList,
          [feePaymentInstructions?.paymentMethodInstructions?.paymentMethod] as string[]
        )[0],
        aba: feePaymentInstructions?.paymentMethodInstructions?.abaNum,
        bankName: feePaymentInstructions?.paymentMethodInstructions?.bankName,
        beneficiaryCity: feePaymentInstructions?.paymentMethodInstructions?.city,
        beneficiaryState: feePaymentInstructions?.paymentMethodInstructions?.state
      };

      const { paymentMethodInstructions } = feePaymentInstructions as FeePaymentInstructions;
      const { address } = paymentMethodInstructions as PaymentMethodInstructions;
      if (paymentMethodInstructions?.payeeName) {
        returnObject = {
          ...returnObject,
          payeeName: paymentMethodInstructions?.payeeName,
          feeReference: paymentMethodInstructions?.referenceOnCheck,
          feeTransactionNarrative: paymentMethodInstructions?.transactionNarrative,
          feeLineOne: address?.addressLineOne,
          feeLineTwo: address?.addressLineTwo,
          feeState: address?.state,
          feeCity: address?.city,
          feePostalCode: address?.postalCode,
          addressType: address?.addressType
        };
      }
    }
  }
  return returnObject;
};

export const getReferenceDataLabel = (refDataList: ReferenceData[], valuesList: string[]): string[] => {
  return refDataList
    .filter((item: ReferenceData) => valuesList?.includes(item.value))
    .map((item: ReferenceData) => item.label);
};

const getSignerPartyDetails = (
  leDocumentSigners: DraftSignerPartyDetails[],
  accounts: DraftAccountDetails[]
): SignerPartyDetails => {
  let returnObject: SignerPartyDetails = {};
  if (accounts) {
    const [accountObject] = accounts;
    const { accountOpeningRequestSigners } = accountObject;
    let signerPartyDetails: SignerPartyDetails = {};
    const clubbedDocumentSigners: DraftSignerPartyDetails[] = [
      ...(leDocumentSigners || []),
      ...((accountOpeningRequestSigners || []))
    ];
    clubbedDocumentSigners?.forEach((signer: DraftSignerPartyDetails) => {
      const keyName: string = getLabelName(signer.documentType);
      const selectedSignerList = [...(signerPartyDetails[keyName]?.signingParty || [])];
      signerPartyDetails = {
        ...signerPartyDetails,
        [keyName]: {
          documentType: signer.documentType,
          signingParty: [
            ...selectedSignerList,
            {
              taxIdentificationNum: signer.taxIdentificationNum,
              externalPartySource: signer.externalPartySource,
              externalPartyId: signer.externalPartyId,
              externalPartyUlek: signer.externalPartyUlek
            }
          ]
        }
      };
    });
    returnObject = { ...signerPartyDetails };
  }
  return returnObject;
};

const getAccountDetails = (accounts: DraftAccountDetails[], referenceData: DataInformation): AccountDetails => {
  const {
    refAssetTypeList,
    refAssetManagementTypes,
    refAssetDenominationTypeList,
    refPaymentMethodTypeList
  } = referenceData;
  let returnObject: AccountDetails = {};
  if (accounts) {
    const [accountObject] = accounts;
    const {
      accountHolding,
      assetManager,
      proxyRecipient,
      accountAccess,
      countryMarketSegregationAccounts
    } = accountObject;

    const account: AccountInfo = {
      name: (accountObject?.accountName as string) || '',
      error: ''
    };

    const [srdContact] = (accountHolding?.srdDisclosureForm?.contactProfile?.contactDetails ||
      []);

    const details: DetailsFormFields = {
      assetType: [...getReferenceDataLabel(refAssetTypeList as ReferenceData[], accountHolding?.assetType as string[])],
      assetTypeList: accountHolding?.assetType as string[],
      other: accountHolding?.otherAssetType,
      assetDenomination: refAssetDenominationTypeList?.find(
        (item: ReferenceData) => item.value === accountHolding?.assetDenomination
      )?.label as string,
      assetDenominationCode: accountHolding?.assetDenomination as string,
      baseCurrency: accountHolding?.currency as string,
      anticipate: accountHolding?.totalAnticipatedTransactionVolume as string,
      accountEstablished: accountHolding?.srdDisclosureForm?.establishedFor as string,
      accountOwner: accountHolding?.srdDisclosureForm?.accountOwnerType,
      preferredContact: accountHolding?.srdDisclosureForm?.contactProfileType,
      contactName: accountHolding?.srdDisclosureForm?.contactProfile?.contactName,
      primaryEmailAddress: srdContact?.primaryEmailAddress
    };

    const assetManagement: AssetManagementFormFields = {
      managementType: refAssetManagementTypes?.find(
        (item: ReferenceData) => item.value === assetManager?.managementType
      )?.label as string,
      managementTypeValue: assetManager?.managementType as string,
      firmName: assetManager?.firmName as string,
      contactName: assetManager?.contactName as string,
      ...getAccountsAddress(assetManager?.address as PartyAddress),
      ...getContactDetails(assetManager?.contactDetails as ContactDetails[]),
      dateToContactAssetManager: assetManager?.firstDateOfContact as string,
      feePaymentCheckbox: assetManager?.assetManagerFeePaymentAuthChkInd as boolean,
      externalPartySource: assetManager?.externalPartySource,
      externalPartyUlek: assetManager?.externalPartyUlek,
      externalPartyId: assetManager?.externalPartyId,
      ...getFeePayment(assetManager as AssetManager, refPaymentMethodTypeList as ReferenceData[])
    };

    const proxyRecipientDetails: ProxyRecipientFormFields = {
      name: proxyRecipient?.contactName,
      ...getAccountsAddress(proxyRecipient?.address as PartyAddress),
      ...getContactDetails(proxyRecipient?.contactDetails as ContactDetails[]),
      externalPartySource: proxyRecipient?.externalPartySource,
      externalPartyUlek: proxyRecipient?.externalPartyUlek,
      externalPartyId: proxyRecipient?.externalPartyId
    };

    const marketAccess: string[] = countryMarketSegregationAccounts as string[];

    const accountAccessList: IAccountAccess[] = accountAccess?.map((access: AccountAccess) => ({
      id: uniqueID(),
      userName: access?.userName as string,
      userId: access?.userId as string,
      passportReporting: access?.passportReporting as string,
      moneyMovement: access?.moneyMovement as boolean,
      tradeOrderEntry: access?.tradeOrderEntry as boolean,
      corporate: access?.corporateActions as boolean
    })) as IAccountAccess[];

    returnObject = {
      account,
      details,
      assetManagement,
      proxyRecipient: proxyRecipientDetails,
      marketAccess,
      accountAccessData: { accountAccess: accountAccessList }
    } as AccountDetails;
  }
  return returnObject;
};

export const getDefaultRecipients = (
  feature: AccountFeatures,
  refFrequencyReceiveTypeList: ReferenceData[]
): IDefaultRecipients[] => {
  const { designatedDelegateIndicator, delegates, defaultRecipients } = feature;

  let returnArray: IDefaultRecipients[] = [
    {
      designateDelegate: designatedDelegateIndicator,
      frequency: [...getReferenceDataLabel(refFrequencyReceiveTypeList, defaultRecipients?.frequency as string[])],
      frequencyCode: defaultRecipients?.frequency
    }
  ];

  if (delegates && Object.keys(delegates).length > 0) {
    returnArray = [
      {
        ...returnArray[0],
        firstName: delegates?.firstName,
        middleName: delegates?.middleName,
        lastName: delegates?.lastName,
        ...getAccountsAddress(delegates?.address as PartyAddress),
        ...getContactDetails(delegates?.contactDetails as ContactDetails[])
      }
    ];
  }
  return returnArray;
};

export const getAdditionalRecipients = (
  delegates: AdditionalRecipients[],
  refFrequencyReceiveTypeList: ReferenceData[]
): IDefaultRecipients[] => {
  let returnArray: IDefaultRecipients[] = [];
  if (delegates?.length > 0) {
    returnArray = delegates?.map((recipient: AdditionalRecipients) => {
      const { frequency, reportingDelegates } = recipient;
      return {
        firstName: reportingDelegates?.firstName,
        middleName: reportingDelegates?.middleName,
        lastName: reportingDelegates?.lastName,
        frequency: [...getReferenceDataLabel(refFrequencyReceiveTypeList, frequency as string[])],
        frequencyCode: frequency,
        ...getAccountsAddress(reportingDelegates?.address as PartyAddress),
        ...getContactDetails(reportingDelegates?.contactDetails as ContactDetails[])
      };
    });
  }
  return returnArray;
};

const getReportingParameters = (
  feature: AccountFeatures,
  refFrequencyReceiveTypeList: ReferenceData[]
): IReportingParameters => ({
  businessDays: feature?.businessDays,
  deliveryStatement: feature?.electronicStatementIndicator,
  defaultRecipients: [...getDefaultRecipients(feature, refFrequencyReceiveTypeList)],
  recipientDetails: [
    ...getAdditionalRecipients(feature?.additionalRecipients as AdditionalRecipients[], refFrequencyReceiveTypeList)
  ]
});

const getTaxParameter = (feature: AccountFeatures, refTaxCostMethodList: ReferenceData[]): ITaxParameter => ({
  taxProfileId: uniqueID(),
  taxProfileName: 'New Tax Profile Name',
  newTaxProfileName: feature?.taxProfileName,
  taxYearEnd: feature?.taxYearEnd,
  marketDiscountAccretionMethodType: feature?.marketDiscountAccretionReportingType,
  marketDiscountAccretionReportingType: feature?.marketDiscountAccretionReportingType,
  marketDiscountAccretionFrequencyType: feature?.marketDiscountAccretionFrequencyType,
  originalIssueDiscountProcessType: feature?.originalIssueDiscountProcessType,
  originalIssueDiscountFrequencyType: feature?.originalIssueDiscountFrequencyType,
  taxExemptPremiumProcessType: feature?.taxExemptPremiumProcessType,
  taxExemptPremiumMethodType: feature?.taxExemptPremiumMethodType,
  taxExemptPremiumFrequencyType: feature?.taxExemptPremiumFrequencyType,
  taxablePremiumProcessType: feature?.taxablePremiumProcessType,
  taxablePremiumMethodType: feature?.taxablePremiumMethodType,
  taxablePremiumFrequencyType: feature?.taxablePremiumFrequencyType,
  taxCostMethodType: refTaxCostMethodList?.find((item: ReferenceData) => item.value === feature?.taxCostMethodType)
    ?.label as string,
  taxCostMethodTypeCode: feature?.taxCostMethodType,
  baseCurrencyForReporting: feature?.baseCurrencyForReporting,
  lotAccountingTypeCodeType: feature?.lotAccountingTypeCodeType,
  baseGlobalCurrencyCodeType: feature?.baseGlobalCurrencyCodeType,
  foreignExchangeEvalForwardsIndicator: feature?.foreignExchangeEvalForwardsIndicator,
  averageCostGainOrLossForCashIndicator: feature?.averageCostGainOrLossForCashIndicator,
  principalAndIncomeRecognitionIndicator: feature?.principalAndIncomeRecognitionIndicator,
  reviewedWithTaxAdvisor: feature?.reviewedWithTaxAdvisor
});

const getFundingDetails = (fundingData: FundingDetails[], refFundingTypeList: ReferenceData[]): IFundingDetails[] => {
  let returnObject: IFundingDetails[] = [];
  if (fundingData) {
    returnObject = fundingData?.map((fund: FundingDetails) => ({
      id: uniqueID(),
      fundingDate: fund?.fundingDate as string,
      fundingTypeCode: refFundingTypeList?.find((item: ReferenceData) => item.value === fund?.fundingTypeCode)
        ?.label as string,
      fundingTypeCodeValue: fund?.fundingTypeCode as string,
      fundingSourceTypeCode: fund?.fundingSourceTypeCode as string,
      accountName: fund?.accountName as string,
      accountNumber: fund?.accountNumber as string,
      institutionName: fund?.institutionName as string
    }));
  }
  return returnObject;
};

const getAccountFeatures = (
  accountFeatures: AccountFeatures[],
  refFrequencyReceiveTypeList: ReferenceData[],
  refTaxCostMethodList: ReferenceData[]
): FeaturesAndServices => {
  let cashSweepOptions: ICashSweep[] = [];
  let reportingParameters: IReportingParameters[] = [];
  let taxParameters: ITaxParameter[] = [];
  let featuresSetDefault: boolean = false;
  accountFeatures?.forEach((feature: AccountFeatures) => {
    featuresSetDefault = feature?.isDefaultAccountFeature as boolean;

    if (feature.accountFeaturesType === RulesType.CASH_SWEEP) {
      cashSweepOptions = [{ portfolio: feature?.portfolio, funds: feature.funds }];
    }

    if (feature.accountFeaturesType === RulesType.REPORTING_PARAMETERS) {
      reportingParameters = [{ ...getReportingParameters(feature, refFrequencyReceiveTypeList) }];
    }

    if (feature.accountFeaturesType === RulesType.TAX_ACCOUNTING_PARAMETER) {
      taxParameters = [{ ...getTaxParameter(feature, refTaxCostMethodList) }];
    }
  });

  return {
    featuresSetDefault,
    cashSweepOptions,
    reportingParameters,
    taxParameters
  };
};

export const getAccountServices = (accountAddOnServices: AccountAddOnServices[]): FeaturesAndServices => {
  let servicesSetDefault: boolean = false;
  let reportingAnalytics: IReportingAnalytics[] = [];
  let dataFeed: IDataFeed[] = [];
  accountAddOnServices?.forEach((service: AccountAddOnServices) => {
    servicesSetDefault = service?.isDefaultAccountFeature as boolean;

    if (service?.accountAddOnType === RulesType.PERFORMANCE_REPORTING_ANALYTICS) {
      reportingAnalytics = [
        {
          benchmarkName: service?.benchmarkName,
          benchmarkCode: service?.benchmarkCode,
          mandateTypeCode: service?.mandateTypeCode,
          performanceBaseCurrencyTypeCode: service?.performanceBaseCurrencyTypeCode,
          otherCurrencyTypeCode: service?.otherCurrencyTypeCode,
          performanceReportingStartDate: service?.performanceReportingStartDate
        }
      ];
    }

    if (service?.accountAddOnType === RulesType.DATA_FEEDS) {
      dataFeed = [
        {
          dataFeedName: service.dataFeedName
        }
      ];
    }
  });
  return {
    servicesSetDefault,
    reportingAnalytics,
    dataFeed
  };
};

export const getFeaturesAndServices = (
  accounts: DraftAccountDetails[],
  referenceData: DataInformation
): FeaturesAndServices => {
  let returnObject: FeaturesAndServices = {};
  if (accounts) {
    const [accountObject] = accounts;
    const { refFrequencyReceiveTypeList, refTaxCostMethodList, refFundingTypeList } = referenceData;
    const { accountFeatures, accountAddOnServices, fundingDetails } = accountObject;

    const { featuresSetDefault, cashSweepOptions, reportingParameters, taxParameters } = getAccountFeatures(
      accountFeatures as AccountFeatures[],
      refFrequencyReceiveTypeList as ReferenceData[],
      refTaxCostMethodList as ReferenceData[]
    );
    const { servicesSetDefault, reportingAnalytics, dataFeed } = getAccountServices(
      accountAddOnServices as AccountAddOnServices[]
    );

    const fundingData: IFundingDetails[] = [
      ...getFundingDetails(fundingDetails as FundingDetails[], refFundingTypeList as ReferenceData[])
    ];

    returnObject = {
      featuresSetDefault,
      cashSweepOptions,
      reportingParameters,
      taxParameters,
      servicesSetDefault,
      reportingAnalytics,
      dataFeed,
      fundingData
    };
  }
  return returnObject;
};

const getPowerHolding = (additionalPowerHoldingRoles: AdditionalPowerHoldingRole[], fiduciary: FiduciaryFields[]) => {
  let returnObject = {};
  if (additionalPowerHoldingRoles?.length) {
    const generatefiduciaryId = (powerHolding: AdditionalPowerHoldingRole): string => {
      let uniqueId: string = uniqueID();
      const powerHoldingObject: OfficerFields | undefined = fiduciary.find((fiduciary: FiduciaryFields) => {
        if (powerHolding?.externalPartySource === ExternalPartySource.EXTERNAL) {
          return fiduciary.externalPartyUlek === powerHolding?.externalPartyUlek;
        } else if (
          powerHolding?.partyType === RulesType.CORPORATE_FIDUCIARY ||
          powerHolding?.partyType === RulesType.INDIVIDUAL_FIDUCIARY
        ) {
          return fiduciary?.taxIdentificationNum === powerHolding?.taxIdentificationNum;
        }
      });
      if (powerHoldingObject) {
        uniqueId = powerHoldingObject?.officerId as string;
      }
      return uniqueId;
    };
    returnObject = {
      powerHoldingRole: additionalPowerHoldingRoles?.map((powerHolding: AdditionalPowerHoldingRole) => ({
        id: generatefiduciaryId(powerHolding),
        name: powerHolding?.name,
        grantorTrust: powerHolding?.grantorSettlerInd,
        initialTrustee: powerHolding?.initialTrusteeInd,
        successorTrustee: powerHolding?.successorTrusteeInd,
        trustAmended: powerHolding?.powerToAmendInd,
        trustRevocable: powerHolding?.powerToRevokeInd,
        taxIdentificationNum: powerHolding?.taxIdentificationNum,
        externalPartySource: powerHolding?.externalPartySource,
        externalPartyUlek: powerHolding?.externalPartyUlek,
        externalPartyId: powerHolding?.externalPartyId,
        partyType: powerHolding?.partyType,
        isFiduciary:
          powerHolding?.partyType === RulesType.CORPORATE_FIDUCIARY ||
          powerHolding?.partyType === RulesType.INDIVIDUAL_FIDUCIARY
      }))
    };
  }
  return returnObject;
};

export const getDataFromDraft = (draftContent: DraftContent, referenceData: DataInformation): CreateDraftDetails => {
  const { officers, beneficialOwners, authorizedAgents, coOwners, fiduciary } = {
    ...getParties(draftContent?.relatedParties)
  };
  const { leDocumentSigners, documentFolderDetails, clientUploadedDocuments, isMaintenanceRequired } = draftContent;
  const { controlPerson, corporateSecretary } = getControlPersonSecretary(
    officers,
    draftContent?.controlPerson as RulesControlPersonSecretary,
    draftContent?.secretary as RulesControlPersonSecretary
  );
  const {
    featuresSetDefault,
    cashSweepOptions,
    reportingParameters,
    taxParameters,
    servicesSetDefault,
    reportingAnalytics,
    dataFeed,
    fundingData
  } = {
    ...getFeaturesAndServices(draftContent?.accounts as DraftAccountDetails[], referenceData)
  };

  return {
    entityDetails: { ...getEntityDetails(draftContent) },
    officers,
    fiduciary,
    controlPerson,
    corporateSecretary,
    beneficialOwners,
    authorizedAgents,
    coOwners,
    accountDetails: {
      ...getAccountDetails(draftContent?.accounts as DraftAccountDetails[], referenceData)
    },
    featuresSetDefault,
    cashSweepOptions,
    reportingParameters,
    taxParameters,
    servicesSetDefault,
    reportingAnalytics,
    dataFeed,
    fundingData,
    signerPartyDetails: {
      ...getSignerPartyDetails(
        leDocumentSigners as DraftSignerPartyDetails[],
        draftContent?.accounts as DraftAccountDetails[]
      )
    },
    documentFolderDetails,
    clientUploadedDocuments,
    isMaintenanceRequired,
    ...getPowerHolding(draftContent?.additionalPowerHoldingRoles || [], fiduciary)
  };
};
